/**
 * Custom hooks for HS Codes API
 *
 * Provides hooks for searching Harmonized System classification codes
 */
import { useCallback, useMemo } from "react";
import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

// ============================================================================
// TYPES
// ============================================================================

export interface HSCodeItem {
  id: number;
  code: string;
  hs_version: string;
  level: number;
  parent_code: string | null;
  section: string | null;
  /** English description — API field name from `/hs-codes` responses. */
  description_en: string;
}

/** Prefer `description_en` (API contract); tolerate legacy `description` if present. */
export function getHsCodeDescription(item: HSCodeItem & { description?: string }): string {
  return item.description_en ?? item.description ?? "";
}

export interface HSCodeWithHierarchy {
  code: HSCodeItem;
  hierarchy: HSCodeItem[];
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const HS_CODES_QUERY_KEYS = {
  all: ["hs-codes"] as const,
  search: (query: string, level?: number) =>
    [...HS_CODES_QUERY_KEYS.all, "search", query, level] as const,
  browse: () => [...HS_CODES_QUERY_KEYS.all, "browse"] as const,
  byCode: (code: string) => [...HS_CODES_QUERY_KEYS.all, code] as const,
  children: (code: string) => [...HS_CODES_QUERY_KEYS.all, code, "children"] as const,
};

// ============================================================================
// SEARCH HS CODES HOOK
// ============================================================================

/**
 * Hook to search HS codes for autocomplete
 *
 * @param query - Search term in description (e.g., "avocado", "coffee")
 * @param level - Optional level filter (2=Chapter, 4=Heading, 6=Subheading)
 * @param enabled - Whether the query should be enabled
 */
export function useHSCodes(
  query: string,
  level?: number,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: HS_CODES_QUERY_KEYS.search(query, level),
    queryFn: async (): Promise<HSCodeItem[]> => {
      const params: Record<string, string | number> = {
        limit: 50,
      };
      if (query) params.q = query;
      if (level) params.level = level;

      return await apiGet<HSCodeItem[]>("/hs-codes", { params });
    },
    enabled: enabled && query.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * First page of HS codes (no search) — for combobox initial list when opening the dropdown.
 */
export function useHSCodesBrowse(enabled: boolean = true) {
  return useQuery({
    queryKey: HS_CODES_QUERY_KEYS.browse(),
    queryFn: async (): Promise<HSCodeItem[]> => {
      return await apiGet<HSCodeItem[]>("/hs-codes", { params: { limit: 80, hs_version: "HS2022" } });
    },
    enabled,
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Descriptions for assigned HS codes: merges browse (limited) with GET /hs-codes/{code}
 * for each code not present in the browse list (e.g. 6-digit codes not in the first page).
 */
export function useHsCodeDescriptionLookup(codes: string[], enabled: boolean) {
  const browseQuery = useHSCodesBrowse(enabled);
  const browseList = browseQuery.data ?? [];

  const codesNeedingDetail = useMemo(() => {
    if (!enabled) return [];
    const browseCodes = new Set(browseList.map((h) => h.code));
    const out: string[] = [];
    const seen = new Set<string>();
    for (const c of codes) {
      const t = c.trim();
      if (!t || seen.has(t) || browseCodes.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
    return out;
  }, [codes, browseList, enabled]);

  const detailQueries = useQueries({
    queries: codesNeedingDetail.map((code) => ({
      queryKey: [...HS_CODES_QUERY_KEYS.byCode(code), "lookup"],
      queryFn: async (): Promise<HSCodeWithHierarchy> => {
        return await apiGet<HSCodeWithHierarchy>(`/hs-codes/${encodeURIComponent(code.trim())}`);
      },
      enabled: enabled && !!code.trim(),
      staleTime: Infinity,
    })),
  });

  const lookup = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of browseList) {
      m.set(h.code, getHsCodeDescription(h));
    }
    codesNeedingDetail.forEach((code, i) => {
      const q = detailQueries[i];
      if (q.data?.code) {
        m.set(code, getHsCodeDescription(q.data.code));
      }
    });
    return m;
  }, [browseList, codesNeedingDetail, detailQueries]);

  const isCodeLoading = useCallback(
    (code: string) => {
      const trimmed = code.trim();
      const idx = codesNeedingDetail.indexOf(trimmed);
      if (idx < 0) return false;
      const q = detailQueries[idx];
      return Boolean(q?.isPending || q?.isFetching);
    },
    [codesNeedingDetail, detailQueries],
  );

  return { lookup, isCodeLoading };
}

// ============================================================================
// GET HS CODE WITH HIERARCHY HOOK
// ============================================================================

/**
 * Hook to fetch an HS code with its full parent hierarchy
 *
 * @param code - HS code (2, 4, or 6 digits)
 * @param enabled - Whether the query should be enabled
 */
export function useHSCodeByCode(code: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: HS_CODES_QUERY_KEYS.byCode(code || ""),
    queryFn: async (): Promise<HSCodeWithHierarchy> => {
      if (!code) throw new Error("No HS code provided");
      return await apiGet<HSCodeWithHierarchy>(`/hs-codes/${code}`);
    },
    enabled: enabled && !!code,
    staleTime: Infinity, // HS codes are static
  });
}

// ============================================================================
// GET HS CODE CHILDREN HOOK
// ============================================================================

/**
 * Hook to fetch children (sub-categories) of an HS code
 *
 * @param code - Parent HS code
 * @param enabled - Whether the query should be enabled
 */
export function useHSCodeChildren(code: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: HS_CODES_QUERY_KEYS.children(code || ""),
    queryFn: async (): Promise<HSCodeItem[]> => {
      if (!code) throw new Error("No HS code provided");
      return await apiGet<HSCodeItem[]>(`/hs-codes/${code}/children`);
    },
    enabled: enabled && !!code,
    staleTime: Infinity,
  });
}
